/**
 * Calculateur de squares — page d'acquisition organique (anglais, marché US).
 *
 * Requêtes visées (Planificateur Google, US, relevé 2026-09-15) :
 * « how many square feet in a roofing square » et ses variantes, 1 000
 * recherches par mois en concurrence faible, plus « how to calculate roof
 * square footage » (1 000) et « how to estimate roof square footage » (880).
 *
 * Le piège que cette page corrige, et qui est la raison d'être du texte :
 * presque tout le monde divise l'EMPREINTE par 100. Un square se compte sur la
 * surface INCLINÉE. À 8/12, l'écart est de 20 %, soit des paquets manquants
 * sur le chantier.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import CoquilleOutil from "@/components/CoquilleOutil";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { multiplicateur, arrondi, millier, PI2_PAR_SQUARE, TABLE_PENTES } from "@/lib/outils";

const URL = "https://roofborder.com/roofing-square-calculator";

const FAQ = [
  {
    q: "How many square feet are in a roofing square?",
    r: "One roofing square is 100 square feet of roof surface. It is the unit shingles, underlayment and labor are priced in, so every supplier quote and every estimate is built on it.",
  },
  {
    q: "Do I count the footprint or the sloped area?",
    r: "The sloped area, always. A 2,000 square foot footprint on an 8/12 roof is 2,404 square feet of actual roof, which is 24 squares rather than 20. Using the footprint underbuys the job by 20 percent at that pitch.",
  },
  {
    q: "How do I calculate roof square footage?",
    r: "Measure the footprint of each roof section, multiply it by the pitch multiplier for that section, and add the sections together. Divide the total by 100 to get squares, then add your waste factor.",
  },
  {
    q: "How much waste should I add?",
    r: "Ten percent is the common figure on a simple gable roof. Fifteen percent is closer to reality on a hip roof or anything with several valleys, dormers or cut-up sections, because every cut leaves an offcut you cannot use.",
  },
  {
    q: "How many bundles is a square of shingles?",
    r: "Three bundles per square for standard three-tab and architectural shingles. Heavier designer profiles run four or five bundles per square, so check the wrapper before you order.",
  },
  {
    q: "Is a roofing square the same as a square of shingles?",
    r: "Yes. A square of shingles covers one roofing square, 100 square feet of roof surface, which is why supplier pricing and estimating software both work in squares.",
  },
];

export default function CalculateurCarres() {
  const [empreinte, setEmpreinte] = useState(2000);
  const [rise, setRise] = useState(6);
  const [perte, setPerte] = useState(10);
  const [pi2Direct, setPi2Direct] = useState("");

  const mult = multiplicateur(Number(rise) || 0, 12);
  // Deux façons d'entrer : soit l'empreinte et la pente (le cas normal), soit
  // une surface inclinée déjà connue, quand le couvreur l'a d'un rapport.
  const surface =
    Number(pi2Direct) > 0 ? Number(pi2Direct) : (Number(empreinte) || 0) * mult;
  const carresBruts = surface / PI2_PAR_SQUARE;
  const carres = carresBruts * (1 + (Number(perte) || 0) / 100);

  const donnees = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.r },
          })),
        },
        {
          "@type": "WebApplication",
          name: "Roofing Square Calculator",
          url: URL,
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          publisher: { "@type": "Organization", name: "Roof Border", url: "https://roofborder.com" },
        },
      ],
    }),
    []
  );

  useSeo({
    titre: "Roofing Square Calculator — Square Feet to Squares | Roof Border",
    description:
      "Free roofing square calculator: turn roof footprint and pitch into sloped square footage, roofing squares and bundles, with a waste factor. One roofing square is 100 square feet of roof surface.",
    canonique: URL,
    donnees,
  });

  const champ =
    "w-full rounded-md border border-line-strong bg-field px-3 py-2 text-base text-ink focus:outline-none focus:ring-2 focus:ring-brand/40";

  return (
    <CoquilleOutil actuel="/roofing-square-calculator">
      <p className="text-xs font-bold tracking-widest uppercase text-brand-ink">Free tool</p>
      <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">Roofing Square Calculator</h1>
      <p className="mt-4 text-lg text-ink-soft max-w-2xl">
        One roofing square is 100 square feet of roof surface — sloped surface, not the footprint
        of the house. Enter the footprint and the pitch and this works out the real area, the
        squares to order and the waste on top.
      </p>

      <section className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-line p-6 bg-surface">
          <h2 className="text-lg font-bold">Your roof</h2>

          <label className="block mt-5 text-sm font-medium" htmlFor="empreinte">
            Footprint (sq ft)
          </label>
          <input
            id="empreinte"
            type="number"
            min="0"
            step="10"
            value={empreinte}
            onChange={(e) => setEmpreinte(e.target.value)}
            className={`mt-1 ${champ}`}
            data-testid="carres-empreinte"
          />

          <label className="block mt-4 text-sm font-medium" htmlFor="rise">
            Pitch (rise over 12)
          </label>
          <input
            id="rise"
            type="number"
            min="0"
            max="18"
            step="0.5"
            value={rise}
            onChange={(e) => setRise(e.target.value)}
            className={`mt-1 ${champ}`}
            data-testid="carres-rise"
          />
          <p className="mt-1 text-xs text-ink-faint">
            Multiplier at {arrondi(Number(rise) || 0, 1)}/12: {arrondi(mult, 3)}.{" "}
            <Link to="/roof-pitch-calculator" className="text-brand-ink hover:underline">
              Not sure of the pitch?
            </Link>
          </p>

          <label className="block mt-6 text-sm font-medium" htmlFor="perte">
            Waste factor (%)
          </label>
          <input
            id="perte"
            type="number"
            min="0"
            max="40"
            step="1"
            value={perte}
            onChange={(e) => setPerte(e.target.value)}
            className={`mt-1 ${champ}`}
            data-testid="carres-perte"
          />

          <label className="block mt-6 text-sm font-medium" htmlFor="direct">
            Or enter sloped area directly (sq ft)
          </label>
          <input
            id="direct"
            type="number"
            min="0"
            step="10"
            placeholder="if you already have it"
            value={pi2Direct}
            onChange={(e) => setPi2Direct(e.target.value)}
            className={`mt-1 ${champ}`}
            data-testid="carres-direct"
          />
          <p className="mt-1 text-xs text-ink-faint">
            Fill this and the footprint and pitch above are ignored.
          </p>
        </div>

        <div className="rounded-xl border border-line p-6 bg-surface" data-testid="carres-resultat">
          <h2 className="text-lg font-bold">Result</h2>
          <dl className="mt-5 space-y-3 text-base">
            <div className="flex justify-between border-b border-line pb-2">
              <dt className="text-ink-soft">Sloped roof area</dt>
              <dd className="font-bold">{millier(surface)} sq ft</dd>
            </div>
            <div className="flex justify-between border-b border-line pb-2">
              <dt className="text-ink-soft">Squares, before waste</dt>
              <dd className="font-bold">{arrondi(carresBruts, 1)} sq</dd>
            </div>
            <div className="flex justify-between border-b border-line pb-2">
              <dt className="text-ink-soft">Squares to order, incl. {arrondi(Number(perte) || 0, 0)} % waste</dt>
              <dd className="font-bold text-xl">{arrondi(carres, 1)} sq</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ink-soft">Bundles at 3 per square</dt>
              <dd className="font-bold">{Math.ceil(carres * 3)}</dd>
            </div>
          </dl>

          <div className="mt-6 rounded-lg bg-hover-strong p-4">
            <p className="text-sm text-ink-soft">
              This assumes one pitch across the whole roof. Real houses have several, and each
              section has its own multiplier.
            </p>
            <Link to="/measure" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:underline">
              Get the real area face by face <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="mt-16 max-w-3xl">
        <h2 className="text-2xl font-extrabold tracking-tight">The mistake that costs bundles</h2>
        <p className="mt-4 text-ink-soft">
          A roofing square is 100 square feet of roof surface, and roof surface is not the same
          thing as the footprint of the house. The steeper the roof, the bigger the gap. Same
          2,000 square foot footprint, three different orders:
        </p>
        <div className="mt-5 overflow-x-auto rounded-xl border border-line">
          <table className="w-full text-sm">
            <thead className="bg-hover-strong text-left">
              <tr>
                <th className="px-4 py-3 font-bold">Pitch</th>
                <th className="px-4 py-3 font-bold">Multiplier</th>
                <th className="px-4 py-3 font-bold">Sloped area</th>
                <th className="px-4 py-3 font-bold">Squares + 10 %</th>
              </tr>
            </thead>
            <tbody>
              {TABLE_PENTES.filter((l) => [0, 4, 6, 8, 10, 12].includes(l.rise)).map((l) => (
                <tr key={l.rise} className="border-t border-line">
                  <td className="px-4 py-2.5 font-medium">{l.ratio}</td>
                  <td className="px-4 py-2.5">{arrondi(l.mult, 3)}</td>
                  <td className="px-4 py-2.5">{millier(2000 * l.mult)} sq ft</td>
                  <td className="px-4 py-2.5">{arrondi((2000 * l.mult * 1.1) / 100, 1)} sq</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-5 text-ink-soft">
          Between a flat roof and a 12/12, the same house swings from 22 to 31.1 squares. That is
          nine squares of shingles, and it is the difference between a job that makes money and one
          that eats a second delivery charge.
        </p>
      </section>

      <section className="mt-16 max-w-3xl">
        <h2 className="text-2xl font-extrabold tracking-tight">How to calculate it by hand</h2>
        <ol className="mt-4 space-y-4 text-ink-soft list-decimal pl-5">
          <li>
            <span className="text-ink font-medium">Break the roof into rectangles and triangles.</span>{" "}
            Measure each one on the ground or off a plan. That gives you the footprint of each
            section in square feet.
          </li>
          <li>
            <span className="text-ink font-medium">Find the pitch of each section.</span> A garage
            or an addition often runs flatter than the main house, and it gets its own multiplier.
          </li>
          <li>
            <span className="text-ink font-medium">Multiply, then add.</span> Footprint times
            multiplier for each section, then total them. That total is your real roof area.
          </li>
          <li>
            <span className="text-ink font-medium">Divide by 100 and add waste.</span> Ten percent
            on a simple gable, fifteen on a cut-up hip roof with valleys and dormers.
          </li>
        </ol>
      </section>

      <section className="mt-16 max-w-3xl">
        <h2 className="text-2xl font-extrabold tracking-tight">Questions</h2>
        <div className="mt-5 space-y-5">
          {FAQ.map((f) => (
            <div key={f.q} className="border-t border-line pt-5">
              <h3 className="font-bold">{f.q}</h3>
              <p className="mt-2 text-ink-soft">{f.r}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-16 rounded-xl border border-line bg-surface p-8 text-center">
        <h2 className="text-2xl font-extrabold tracking-tight">Skip the rectangles</h2>
        <p className="mt-3 text-ink-soft max-w-xl mx-auto">
          Put in an address. Roof Border measures every roof face and its pitch from satellite
          imagery, totals the squares for you and prices the job.
        </p>
        <Link to="/measure">
          <Button className="mt-6 rounded-md h-12 px-7 text-base">
            Measure a roof <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </section>
    </CoquilleOutil>
  );
}
